<template>
	<article class="d-overview-list">
		<PageHeader :intro="intro" layout="list" class="mb-64 >=768:mb-3xl/v">
			<template #afterTeaser>
				<div
					class="s-rich-text s-rich-text--monotone"
					:class="intro.teaser && 'mt-sm/v'"
					v-html="text"
				></div>
			</template>
		</PageHeader>

		<!-- Desktop grid -->
		<div
			v-if="children.length"
			class="
				px-layout-margin
				hidden
				>=768:grid
				gap-x-sm/h gap-y-md/v
				grid-cols-1
				>=768:grid-cols-2
				>=1024:grid-cols-3
			"
		>
			<CardItem
				v-for="child in children"
				:key="child.id"
				:heading-level-offset="1"
				v-bind="child"
			/>
		</div>

		<!-- Mobile accordion, specific for this page type so built in-situ -->
		<div
			ref="accordionGroup"
			class=">=768:hidden overflow-hidden px-layout-margin"
		>
			<div class="w-full isolate bg-white text-text">
				<LinkTile
					v-for="(child, index) in children"
					:id="`overview-list-${id}-${child.id}__accordion-item`"
					:key="child.id"
					:to="child.url"
					:aria-labelledby="`overview-list-${id}-${child.id}__name`"
					class="
						d-overview-list__accordion-item
						border border-solid border-gray-light
					"
					:class="{
						'-mt-1': index > 0,
					}"
				>
					<!-- Header -->
					<div class="flex items-center w-full px-2xs/h py-3xs/v">
						<!-- Icon -->
						<AreaIcon
							v-if="child.iconUrl"
							class="w-32 h-32 mr-2xs/h flex-shrink-0"
							:src="child.iconUrl"
						/>

						<!-- Text -->
						<div class="mr-2xs/h">
							<small v-if="child.label && !child.iconUrl">
								<div
									v-if="child.label"
									:id="`overview-list-${id}-${child.id}__label`"
									class="
										font-medium
										uppercase
										text-label-sm text-theme
									"
									v-text="child.label"
								></div>
							</small>

							<BaseH4
								v-if="child.name"
								:id="`overview-list-${id}-${child.id}__name`"
								:level-offset="1"
								class="text-card-title-xs py-5xs/v"
								v-html="child.name"
							/>
						</div>

						<!-- Toggle -->
						<AccordionHeader
							v-if="
								child.teaser || (child.image && !child.iconUrl)
							"
							:id="`overview-list-${id}-${child.id}__header`"
							:class="[
								'relative ml-auto w-40 h-40 p-0 flex-shrink-0',
								'flex items-center justify-center',
								'rounded-full border border-theme',
								'transition duration-200 ease-in-out',
								{
									'bg-theme hover:bg-theme-90 text-white transform -rotate-180':
										panelsOpen[index],
									'text-theme hover:bg-theme hover:text-white':
										!panelsOpen[index],
								},
							]"
							:aria-labelledby="`overview-list-${id}-${child.id}__name overview-list-${id}-${child.id}__header`"
							:aria-controls="`overview-list-${id}-${child.id}__panel`"
							@click.stop
						>
							<SvgIconWedge
								class="w-10 h-8 transform translate-y-1"
							/>
						</AccordionHeader>
					</div>

					<!-- Panel -->
					<AccordionPanel
						v-if="child.teaser || (child.image && !child.iconUrl)"
						:id="`overview-list-${id}-${child.id}__panel`"
						:ref="`panel-${index}`"
						:transition="{ name: 't-accordion' }"
						@change="(event) => onPanelChange(event, index)"
					>
						<div>
							<div
								v-if="child.teaser"
								class="px-2xs/h"
								:class="
									child.image && !child.iconUrl
										? 'pb-sm/v'
										: 'pb-lg/v'
								"
							>
								<p
									class="
										font-light
										text-body-xs
										whitespace-pre-wrap
									"
									v-html="child.teaser"
								></p>
							</div>

							<BaseImg
								v-if="child.image && !child.iconUrl"
								class="w-full"
								:src="child.image.cropUrl || child.image.url"
								:alt="child.image.altText"
								:sizes="nuxtImgSizes('<1441:443px')"
								:width="child.image.width"
								:height="child.image.height"
								:ratio="443 / 174"
							/>
						</div>
					</AccordionPanel>
				</LinkTile>
			</div>
		</div>
	</article>
</template>

<script>
import PageHeader from '~/components/main/PageHeader';
import CardItem from '~/components/main/CardItem';
import LinkTile from '~/components/shared/LinkTile';
import { AccordionHeader, AccordionPanel } from '~/components/shared/Accordion';
import AreaIcon from '~/components/main/AreaIcon';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';
import { nuxtImgSizes } from '~/assets/js/utilities/helpers';

export default {
	name: 'MainOverviewList',
	components: {
		PageHeader,
		CardItem,
		LinkTile,
		AccordionHeader,
		AccordionPanel,
		AreaIcon,
		SvgIconWedge,
	},
	inheritAttrs: false,

	props: {
		id: Number,
		intro: Object,
		text: String,
		children: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			panelsOpen: this.children.map(() => false),
		};
	},

	methods: {
		nuxtImgSizes,

		onPanelChange(event, index) {
			this.$set(this.panelsOpen, index, event.isOpen);
		},
	},
};
</script>

<style lang="postcss">
.d-overview-list {
	@apply text-white;
}
.d-overview-list:before {
	@apply bg-theme;
	content: '';
	display: block;
	position: fixed;
	left: -50vw; /* for some reason blur triggers weird stuff if just 0 0 and 100vw and vh */
	top: 0;
	width: 200vw;
	height: calc(
		100vh + 9999999px
	); /* and for some reason the blur cuts the bottom of the page if just 100vh / treats it as semi-absolute? */
	z-index: -1;
}

.d-overview-list__accordion-item {
	&:before,
	&:after {
		@apply absolute left-0 top-0 w-full h-full;
		@apply transition-opacity duration-200 opacity-0 pointer-events-none;
		content: '';
	}
	&:before {
		@apply bg-theme-40 bg-opacity-20;
		z-index: -1;
	}
	&:after {
		@apply border border-solid border-theme-40;
		top: -0.99px;
		left: -0.99px;
		width: calc(100% + 1.99px);
		height: calc(100% + 1.99px);
		z-index: 2;
	}
	&:hover:before,
	&:focus-within:before,
	&:hover:after,
	&:focus-within:after {
		@apply opacity-100;
	}
}
</style>
